<template>
  <div
    class="wishlist-collection"
    :style="{ background: background }"
  >
    <div class="w-100 d-block">
      <div class="d-flex wishlist-collection-header-info">
        <div class="wishlist-collection-header-user d-flex">
          <div class="username-icon">
            {{ usernameAbreviation }}
          </div>
          <div class="ml-2">
            {{ ownerUserName }}
          </div>
        </div>
        <div class="wishlist-collection-header-actions d-flex">
          <div
            v-if="isOwner && !isSavedForLaterList"
            class="wishlist-action-item wishlist-action-button"
            @click.prevent="openEditModal"
          >
            <EditIcon class="header-icon" />
            <div class="wishlist-action-text d-none d-lg-inline-block">
              Edit
            </div>
          </div>
          <div
            v-else-if="!!currentUser && !isSavedForLaterList"
            class="wishlist-action-item wishlist-action-button"
            @click.prevent="openDuplicateModal"
          >
            <AddIcon class="header-icon" />
            <div class="wishlist-action-text d-none d-lg-inline-block">
              Duplicate this wishlist
            </div>
          </div>
          <!-- Share should be available to public lists-->
          <ShareModal
            v-if="!isPrivate && !isSavedForLaterList"
            class="wishlist-action-item wishlist-action-button"
            :shareable-text="shareableLink"
            title="Share this wishlist"
            wrapper-class="modal-wishlists"
            label-text="Or copy link"
            :params="socialParams"
          />
        </div>
      </div>
      <div class="d-block wishlist-collection-header-content">
        <h1>{{ name }}</h1>
        <p v-if="description">
          {{ description }}
        </p>
      </div>

      <!-- Edit/Delete/Duplicate - only render if user is logged in -->
      <DetailModal
        v-if="currentUser"
        :show-modal.sync="showWishlistModal"
        :description="description"
        :is-editing="isEdit"
        :is-duplicating="isDuplicate"
        :name="name"
        :wishlist-id="headerData.id"
        :is-private="isPrivate"
        @wishlistUpdated="onwishlistUpdated"
      />
    </div>
  </div>
</template>
<script>
import AddIcon from '~/static/add.svg?inline';
import EditIcon from '~/static/edit.svg?inline';
import DetailModal from '../wishlists/DetailModal.vue';
import ShareModal from '../common/ShareModal.vue';
import { lightenColor, getUtmParamsForSocialShare } from '~/util/global';

export default {
  components: {
    AddIcon,
    EditIcon,
    DetailModal,
    ShareModal,
  },
  props: {
    collection: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showWishlistModal: false,
      isEdit: false,
      isDuplicate: false,
    };
  },
  async fetch() {
    await this.$store.dispatch('wishlists/getWishlistsHeaderData', this.templateSlug);
  },
  computed: {
    description() {
      return this.headerData?.description;
    },
    isSavedForLaterList() {
      return !!this.headerData?.is_save_for_later;
    },
    name() {
      return this.headerData?.name;
    },
    isPublic() {
      return !!this.headerData?.is_public;
    },
    isPrivate() {
      return !this.headerData?.is_public;
    },
    templateSlug() {
      return this.collection?.template_slug;
    },
    headerData() {
      return this.$store.getters['wishlists/wishlist'] || {};
    },
    isOwner() {
      return !!this.headerData?.is_owner;
    },

    ownerUserName() {
      return this.headerData?.user_name || 'Sumo-ling';
    },

    currentUser() {
      return this.$store.state.session.user;
    },

    shareableLink() {
      if (process.client) {
        return `${window.location.hostname}/collections/${this.collection.slug}`;
      }

      return '';
    },

    requestErrorMessage() {
      return this.$store.getters['wishlists/errorMessage'];
    },

    isProcessing() {
      return this.$store.getters['wishlists/isProcessing'];
    },

    usernameAbreviation() {
      const userName = this.ownerUserName;
      if (userName && userName.length >= 2) {
        const names = userName.split(' ');

        if (names.length > 1) {
          const abv = names[0].slice(0, 1) + names[1].slice(0, 1);
          return abv.toUpperCase();
        }
        return userName.slice(0, 2).toUpperCase();
      }
      return 'UN';
    },
    background() {
      if (this.collection.header_background) {
        const dk = this.collection.header_background.replace('#', '');
        const lt = lightenColor(dk, 25);
        return `radial-gradient(58.66% 71.5% at 64.93% 27.87%, #${lt} 24.72%, #${dk} 100%)`;
      }

      return null;
    },

    socialParams() {
      return getUtmParamsForSocialShare(this.headerData, this.currentUser?.id);
    },
  },

  mounted() {
    // If an anonymous user is viewing the wishlist collection
    //  Ask them to sign in or signup to add to their wishlist
    if (!this.currentUser) {
      this.$toastr.i({
        msg: '<strong>Log in</strong> or <strong>sign up</strong> to add these products to your wishlist',
        timeout: 0,
        /* Secondary/Bolt #0E6EF4 -- from figma */
        style: { backgroundColor: '#0E6EF4', width: '380px' },
      });
    }
  },

  methods: {
    openEditModal() {
      this.isEdit = true;
      this.isDuplicate = false;
      this.showWishlistModal = true;
    },

    openDuplicateModal() {
      this.isEdit = false;
      this.isDuplicate = true;
      this.showWishlistModal = true;
    },

    onwishlistUpdated() {
      this.isEdit = false;
      this.isDuplicate = false;
      this.showWishlistModal = false;
    },
  },
};
</script>
<style lang="scss" scoped>
  @import '~/styles/dorado/main';
  @import '~/styles/utils/breakpoints';

  .wishlist-collection {
    background: radial-gradient(58.66% 71.5% at 64.93% 27.87%, #265e60 24.72%, #09292b 100%);
    border-radius: 4px;
    width: 100%;
    padding: 24px 32px 32px;
    margin-bottom: 32px;
    display: flex;
  }

  .wishlist-collection::v-deep {
    .modal-wishlists .modal-title {
      color: $dark;
    }
  }

  .wishlist-collection > div:first-child {
    flex-grow: 1;
  }

  @include media-breakpoint-down(sm) {
    .wishlist-collection {
      margin-top: 20px;
      margin-bottom: 24px;
      padding: 16px;
    }
  }

  .wishlist-action-item ::v-deep {
    .header-icon {
      height: 16px;
      width: 20px;
      padding-right: 4px;

      > path {
        fill: #fff;
      }
    }
  }

  .username-icon {
    border-radius: 50%;
    background: $spruce;
    height: 32px;
    width: 32px;
    text-align: center;
  }

  .wishlist-action-button {
    cursor: pointer;
  }

  .wishlist-action-item {
    border: 1px solid $gray-700;
    border-radius: 4px;
    padding: 2px 8px;
  }

  .wishlist-action-text {
    font-size: 16px;
  }

  .wishlist-collection-header-actions {
    color: #fff;

    .wishlist-action-item:not(:first-child) {
      margin-left: 1rem;
    }
  }

  .wishlist-collection-header-user {
    color: #fff;
  }

  .wishlist-collection-header-content {
    h1 {
      color: #fff;
      font-weight: 700;
      font-size: 32px;
      line-height: 48px;
    }

    p {
      color: #fff;
      font-weight: 400;
      font-size: 16px;
      line-height: 24px;
    }
  }

  .wishlist-collection-header-info {
    display: flex;
    justify-content: space-between;
    line-height: 32px;
  }

  @include media-breakpoint-down(lg) {
    .wishlist-action-item ::v-deep {
      .header-icon {
        padding-right: 0;
      }
    }
  }
</style>
